<template>
    <div
        ranking
        question
        :id="`question-${questionData.number}`"
        @keyup.enter="submitQuestion"
    >
        <questionheader :questionData="questionData" />
        <div data-question-main>
            <div answers-container>
                <draggable
                    v-model="questionData.answers"
                    draggable=".listitem"
                    handle="[role='drag']"
                    group="answers"
                    @end="dragged"
                >
                    <div
                        v-for="(answer, index) in questionData.answers"
                        :key="`answer-${answer.id}`"
                        class="listitem"
                        data-answer
                        role="listitem"
                        :id="`question-${questionData.number}_answer-${answer.number}`"
                        :is-checked="answer.isChecked"
                        :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
                    >
                        <div data-index data-answer-part>
                            <select
                                :value="answer.openField === '' ? '-' : answer.openField"
                                @change="selectChanged(answer, index, $event)"
                                @keydown.enter.exact.stop
                                @keyup.enter.exact.stop
                                :id="`question-${questionData.number}_number-${answer.number}`"
                                :required="answersStillRequired"
                                :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
                                focus-input 
                            >
                                <option value="-">-</option>
                                <option
                                    v-for="n in questionData.answers.length"
                                    :key="`rank-${n}`"
                                    :value="n"
                                >
                                    {{ n }}
                                </option>
                            </select>
                        </div>
                        <div data-right-label>
                            <div data-label data-answer-part>
                                <p
                                    v-html="DOMPurify.sanitize(answer.label)"
                                    :id="`question-${questionData.number}_label-${answer.number}`"
                                ></p>
                            </div>
                        </div>
                        <div data-answer-actions>
                            <div
                                data-drag-part
                                role="drag"
                            >
                                <div 
                                    data-drag-icon                            
                                    aria-hidden="true"
                                >
                                </div>
                            </div>
                        </div>
                    </div>
                </draggable>
            </div>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import draggable from "vuedraggable";

export default {
    name: `ranking-question`,
    mixins: [mixins.defaultQuestion],
    inject: ['DOMPurify'],
    components: {
        draggable,
    },
    computed: {
        // The answers that are selected by the user
        answersGiven: function () {
            return this.questionData.answers.filter((answer) => answer.isChecked).length;
        },
        // Decide whether the options have to have a required attribute added to them. In this way we can trigger
        // the browser to consider the form to be invalid as long as the right amount of options hasn't been selected
        answersStillRequired: function () {
            // Is the amount of selected within both the upper and lower limits that are allowed?
            let maxAmtValid, minAmtValid;
            let maxRestrictValue = parseInt(this.restrictions.maxValue);
            let minRestrictValue = parseInt(this.restrictions.minValue);

            if (maxRestrictValue === -1) maxAmtValid = false;
            else maxAmtValid = this.answersGiven > maxRestrictValue;

            if (minRestrictValue === -1) minAmtValid = false;
            else minAmtValid = this.answersGiven < minRestrictValue;

            return minAmtValid || maxAmtValid;
        },
    },
    methods: {
        dragged(evt) {
            const movedItem = this.questionData.answers[evt.newIndex];

            // // Was item allready answered?
            // const alreadyChecked = movedItem.isChecked;

            // all answered items.
            const rankedItems = this.questionData.answers.filter(
                answer => answer.isChecked && answer.id !== movedItem.id
            );

            let newRank = Math.min(evt.newIndex + 1, rankedItems.length + 1);

            // item checked as answered.
            movedItem.isChecked = true;

            // remove answer first
            const currentIndex = this.questionData.answers.findIndex(
                a => a.id === movedItem.id
            );

            this.questionData.answers.splice(currentIndex, 1);

            // look for position inside of the answered items.
            let insertIndex = rankedItems.length;

            if (newRank <= rankedItems.length) {
                const targetItem = rankedItems[newRank - 1];

                insertIndex = this.questionData.answers.findIndex(
                    a => a.id === targetItem.id
                );
            }

            // put it back on the right position.
            this.questionData.answers.splice(insertIndex, 0, movedItem);

            // and rebuild answers.
            this.updateRankings();
        },
        selectChanged(answer, currentIndex, e) {
            const value = e.target.value;

            if (value === '' || value === '-') {
                answer.isChecked = false;
                answer.openField = '';

                // move to unanswered answers
                const currentPos = this.questionData.answers.findIndex(
                    a => a.id === answer.id
                );

                const item = this.questionData.answers.splice(currentPos, 1)[0];

                this.questionData.answers.push(item);

                this.updateRankings();

                return;
            }

            const rank = parseInt(value);

            // remove from current position
            const oldIndex = this.questionData.answers.findIndex(
                a => a.id === answer.id
            );

            const movedItem = this.questionData.answers.splice(oldIndex, 1)[0];

            movedItem.isChecked = true;

            let insertIndex = 0;

            if (rank === 1) {
                insertIndex = 0;
            }
            else {
                const rankedItems = this.questionData.answers.filter(
                    a => a.isChecked
                );

                if (rank - 2 < rankedItems.length) {
                    const previousRankedItem = rankedItems[rank - 2];

                    insertIndex = this.questionData.answers.findIndex(
                        a => a.id === previousRankedItem.id
                    ) + 1;

                } else {
                    insertIndex = rankedItems.length;
                }
            }

            // insert new position
            this.questionData.answers.splice(insertIndex, 0, movedItem);

            this.updateRankings();
        },
        updateRankings() {
            let rank = 1;

            this.questionData.answers.forEach(answer => {
                if (answer.isChecked) {
                    answer.openField = rank.toString();
                    rank++;
                } else {
                    answer.openField = '';
                }
            });
        }
    }
};
</script>

<style scoped>
@import "./../styles/variables.css";

[ranking] {
    [data-question-main] {
        width: 100%;
    }

    [answers-container] {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    [data-answer] {
        margin-block: 10px;
        padding-inline-start: 20px;
        display: flex;
        border: 1px solid var(--border-black-color-25);
        border-radius: 16px;
        cursor: default;

        &[disabled] {
            background: #e4e4e4;
            pointer-events: none;
            opacity: 0.5;
        }
    }

    [data-answer-actions] {
        display: flex;
        align-items: center;
        padding-right: 20px;
    }

    [data-drag-part] {
        cursor: grab;
        width: 44px;
        height: 44px;
        margin: 18px 0;
        display: flex;
        align-items: center;
        justify-content: center;

        [data-drag-icon] {
            background:
                radial-gradient(circle at 5px 5px, #6d6d6d 1px, transparent 2px)
                0 0 / 7px 7px;
            width: 16px;
            height: 23px;
            pointer-events: none;
        }
    }

    [data-right-label] {
        min-height: 60px;
        width: 100%;
        display: flex;

        p {
            font-size: 15px;
            color: #000;
            margin: 0;
            font-weight: bold;
            pointer-events: none;
            user-select: none;
        }

        [data-label] {
            width: 100%;
            padding: 20px;
            margin-left: 10px;
        }
    }

    [data-answer-part] {
        color: #000;
        display: flex;
        align-items: center;
        border-radius: 5px;
        transition: 0.2s;
    }

    [data-index] {
        justify-content: center;
        min-width: 58px;
        border: 1px solid #BDBDBD;
        border-radius: 8px;
        select {
            border: none;
            background: transparent;
            color: #000;
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            cursor: pointer;
            padding-block: 11px;
            padding-inline: 11px;
        }
    }

    [is-checked] {
        background: var(--primary-color-15);
        border: 1px solid var(--border-black-color-25);
        [data-index] {
            background: #FFFFFF4D;
        }
        [data-answer-part] {
            p {
                color: #000;
            }

            select {
                color: #000;
            }
        }
    }
}

@media only screen and (max-width: 550px) {
    [ranking] {
        [data-answer] {
            [data-right-label] {
                [data-label] {
                    padding: 10px;
                }
            }
        }
        [data-answer-actions] {
            padding-right: 5px;
        }
    }
}
</style>